const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/labReportsUiActions.ts';
import Imm from 'immutable';
import { Action, Dispatch } from 'redux';

export const actionTypes = {
    launchLabReportsPage: 'COMMON_LAUNCH_LABREPORTS_PAGE',
    selectLabReport: 'COMMON_SELECT_LABREPORT',
    setOptions: 'COMMON_LAB_SET_OPTIONS',
    setVisibleMaxDat: 'COMMON_SET_LABREPORT_VISIBLE_MAXDAT',
    setCheckedLabResults: 'COMMON_SET_CHECKED_LABRESULTS',
    toggleLabResultChecked: 'COMMON_TOGGLE_LABRESULT_CHECKED',
    setSuppressSnackbars: 'COMMON_SET_SUPPRESS_LABREPORT_SNACKBARS',
    showPreviousResults: 'COMMON_SHOW_PREVIOUS_LABRESULTS',
    showNextResults: 'COMMON_SHOW_NEXT_LABRESULTS',
};

export interface ILaunchLabReportsPageAction extends Action {}

/**
 * Launches the lab reports page
 * @return: A thunk action function.
 */
export function launchLabReportsPage(navigate: any, changeRoute: boolean = true) {
    // Using thunk.
    return (dispatch: Dispatch<any>) => {
        const a: ILaunchLabReportsPageAction = {
            type: actionTypes.launchLabReportsPage,
        };
        dispatch(a);

        if (changeRoute) {
            // FIXME: this should never be in common.... w t f
            navigate('/app/lab-reports/table');
        }
    };
}

/**
 * Marks a lab report as selected. It is always assumed the report belongs to the currently selected patient.
 * @param instanceId id for this lab reports view
 * @param defsId Id of the report's cumulDefs
 * @return: An action object.
 */
export function selectLabReport(instanceId: string, defsId: string) {
    return {
        type: actionTypes.selectLabReport,
        instanceId,
        defsId,
    };
}

export type SelectLabReportAction = ReturnType<typeof selectLabReport>;

/**
 * Sets the options how lab report data is to be visualized.
 * @param instanceId id for this lab reports view
 * @param numColumns the number of columns of the table; 0=auto
 * @param requiredFlags if specified and non-empty, only values having one of the required flags will be shown
 * @param mapIcode icode to be used for mapping-based reports
 * @return an action object
 */
export function setOptions(instanceId: string, numColumns: number, requiredFlags: string[], mapIcode: string) {
    return {
        type: actionTypes.setOptions,
        instanceId,
        numColumns,
        requiredFlags,
        mapIcode,
    };
}

export type SetOptionsAction = ReturnType<typeof setOptions>;

/**
 * Sets a new visibleMaxDat to be used with the currently selected report. The visibleMaxDat is the upper limit above
 * which no results are displayed.
 * @param instanceId id for this lab reports view
 * @param maxdat YYYY-MM-DD HH:mm:ss or null
 * @param actor Specifies who is calling this action. Determines what the sagas should do about it.
 *    - 'ui': The UI is calling this action. Sagas should listen to this and load summaries/column data if necessary.
 *    - 'saga': One of the sagas is calling this action. The saga is responsible for doing everything required.
 */
export function setVisibleMaxDat(instanceId: string, maxdat: string, actor: 'ui' | 'saga') {
    return {
        type: actionTypes.setVisibleMaxDat,
        instanceId,
        maxdat,
        actor,
    };
}

export type SetVisibleMaxDatAction = ReturnType<typeof setVisibleMaxDat>;

/**
 * Sets the list of lab results to be the given list of rowIds.
 * @param instanceId id for this lab reports view
 * @param rowIds may be null
 */
export function setCheckedLabResults(instanceId: string, rowIds: Imm.List<any> | null) {
    return {
        type: actionTypes.setCheckedLabResults,
        instanceId,
        rowIds,
    };
}

export type SetCheckedLabResultsAction = ReturnType<typeof setCheckedLabResults>;

/**
 * Toggles the checkmark associated with the lab result of the given id.
 * @param instanceId id for this lab reports view
 * @param rowId LabId from rowDefs
 * @param pos The position after which the new checkmark flag is to be inserted. The position
 *    of the checkmark will directly correspond to the ordering of the charts on the page. Set this
 *    argument to null to add the checkmark at the beginning. This parameter is ignored if the
 *    checkmark is currently checked, i.e. if the checkmark is to be removed rather than inserted.
 */
export function toggleLabResultChecked(instanceId: string, rowId: string, pos: number | null) {
    return {
        type: actionTypes.toggleLabResultChecked,
        instanceId,
        rowId,
        pos,
    };
}

export type ToggleLabResultCheckedAction = ReturnType<typeof toggleLabResultChecked>;

/**
 * Enabled or disables snackbars invoked by the sagas.
 */
export function setSuppressSnackbars(suppress: boolean) {
    return {
        type: actionTypes.setSuppressSnackbars,
        suppress,
    };
}

export type SetSuppressSnackbarsAction = ReturnType<typeof setSuppressSnackbars>;

/**
 * Causes the view to advance to the previous results.
 * @param instanceId id for this lab reports view
 * @param numVisibleColumns The number of columns visible with the current device type and orientation.
 */
export function showPreviousResults(instanceId: string, numVisibleColumns: number) {
    return {
        type: actionTypes.showPreviousResults,
        instanceId,
        numVisibleColumns,
    };
}

export type ShowPreviousResultsAction = ReturnType<typeof showPreviousResults>;

/**
 * Causes the view to advance to the next results.
 * @param instanceId id for this lab reports view
 * @param numVisibleColumns The number of columns visible with the current device type and orientation.
 */
export function showNextResults(instanceId: string, numVisibleColumns: number) {
    return {
        type: actionTypes.showNextResults,
        instanceId,
        numVisibleColumns,
    };
}

export type ShowNextResultsAction = ReturnType<typeof showNextResults>;
